import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import TriggerEditor from '../components/TriggerEditor';
import './ProgramDetails.css';

interface Partner {
  id: number;
  name: string;
  description: string;
  address: string;
  code: string;
}

interface Trigger {
  id?: number;
  name: string;
  action: string;
  detail?: string;
  operator: string;
  value: number | string;
  unit?: string;
  aggregation_type?: string;
  time_window?: number;
  group_by?: string;
  relationship?: 'AND' | 'OR';
  next_trigger_id?: number | null;
  NextTrigger?: Trigger;
}

interface Precondition {
  id: number;
  type: string;
  value: string;
  listOfConditions: any;
}

interface Program {
  id: number;
  name: string;
  description: string;
  value_paid: number;
  date_start: string;
  date_end: string | null;
  partner_id: number;
  partner: Partner;
  Triggers?: Trigger[];
  Preconditions?: Precondition[];
  partner_program_id?: string;
  trigger_once: boolean;
}

const ProgramDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const programId = id || '';
  
  const [program, setProgram] = useState<Program | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'details' | 'triggers' | 'preconditions'>('details');
  const [editingTrigger, setEditingTrigger] = useState<boolean>(false);
  const [triggerSaved, setTriggerSaved] = useState<boolean>(false);
  const [currentTriggerId, setCurrentTriggerId] = useState<number>(0);
  const [programTriggers, setProgramTriggers] = useState<Trigger[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Fetch program details
  const fetchProgram = async () => {
    try {
      const response = await axios.get(`/api/programs/${programId}`);
      
      // Normalize the data to handle different field naming conventions
      const rawValuePaid = response.data.value_paid || response.data.valuePaid;
      
      const programData = {
        ...response.data,
        date_start: response.data.date_start || response.data.dateStart,
        date_end: response.data.date_end || response.data.dateEnd,
        value_paid: typeof rawValuePaid === 'number' ? rawValuePaid : parseFloat(rawValuePaid || '0'),
        partner_id: response.data.partner_id || response.data.partnerId,
        trigger_once: response.data.trigger_once || false
      };
      
      console.log('Program data normalized:', programData);
      setProgram(programData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching program:', error);
      setError('Failed to load program details');
      setLoading(false);
    }
  };
  
  // Fetch all triggers for this program
  const fetchProgramTriggers = async () => {
    try {
      console.log(`Fetching trigger chains for program ${programId}`);
      
      // Use the API URL without hardcoding the server address
      const response = await axios.get(`/api/programs/${programId}/trigger-chains`);
      console.log('Trigger chains response:', response.data);
      setProgramTriggers(response.data);
    } catch (error) {
      console.error('Error fetching program triggers:', error);
      setError('Failed to fetch program triggers');
    }
  };
  
  // Load data on component mount
  useEffect(() => {
    fetchProgram();
    fetchProgramTriggers();
  }, [programId]);
  
  // Handle editing a trigger
  const handleEditTrigger = (triggerId: number) => {
    setCurrentTriggerId(triggerId);
    setEditingTrigger(true);
  };
  
  // Handle adding a new trigger
  const handleAddTrigger = () => {
    setCurrentTriggerId(0);
    setEditingTrigger(true);
  };
  
  // Handle trigger saved callback
  const handleTriggerSaved = () => {
    setEditingTrigger(false);
    fetchProgramTriggers();
    setTriggerSaved(true);
    setTimeout(() => setTriggerSaved(false), 3000);
  };
  
  // Handle cancel trigger edit
  const handleCancelTriggerEdit = () => {
    setEditingTrigger(false);
  };
  
  // Handle removing a trigger
  const handleRemoveTrigger = async (triggerId: number) => {
    if (window.confirm('Are you sure you want to remove this trigger?')) {
      try {
        await axios.delete(`/api/triggers/${triggerId}`);
        fetchProgramTriggers();
        setSuccessMessage('Trigger removed successfully!');
        setTimeout(() => setSuccessMessage(null), 3000);
      } catch (error) {
        console.error('Error removing trigger:', error);
        setError('Failed to remove trigger');
      }
    }
  };
  
  // Helper function to render a trigger chain
  const renderTriggerChain = (trigger: Trigger) => {
    if (!trigger) return null;
    
    return (
      <div className="trigger-chain-item">
        <div className="trigger-item">
          <div className="trigger-content">
            <div className="trigger-header">
              <h5 className="trigger-name">{trigger.name}</h5>
              <div className="trigger-actions">
                <button
                  className="edit-button"
                  onClick={() => handleEditTrigger(trigger.id || 0)}
                >
                  Edit
                </button>
                <button
                  className="remove-button"
                  onClick={() => handleRemoveTrigger(trigger.id || 0)}
                >
                  Remove
                </button>
              </div>
            </div>
            
            <div className="trigger-rule">
              <span className="trigger-action">{trigger.action}</span>
              {trigger.detail && (
                <span className="trigger-detail">({trigger.detail})</span>
              )}
              <span className="trigger-operator">{trigger.operator}</span>
              <span className="trigger-value">{trigger.value} {trigger.unit}</span>
              {trigger.aggregation_type && trigger.aggregation_type !== 'none' && (
                <span className="trigger-aggregation">
                  (Aggregated by {trigger.aggregation_type}
                  {trigger.time_window && ` over ${trigger.time_window} days`}
                  {trigger.group_by && `, grouped by ${trigger.group_by}`})
                </span>
              )}
            </div>
          </div>
        </div>
        
        {trigger.NextTrigger && (
          <div className="trigger-relationship">
            <div className={`relationship-indicator ${trigger.relationship?.toLowerCase() || 'and'}`}>
              {trigger.relationship || 'AND'}
            </div>
            {renderTriggerChain(trigger.NextTrigger)}
          </div>
        )}
      </div>
    );
  };
  
  if (loading) {
    return <div className="loading">Loading program details...</div>;
  }
  
  if (error || !program) {
    return (
      <div className="error-container">
        <h2>Error</h2>
        <p>{error || 'Program not found'}</p>
        <button onClick={() => navigate('/programs')}>
          Back to Programs
        </button>
      </div>
    );
  }
  
  return (
    <div className="program-details-container">
      <div className="program-header">
        <h1>{program?.name || 'Program Details'}</h1>
        <div className="program-actions">
          <button onClick={() => navigate('/programs')}>
            Back to Programs
          </button>
        </div>
      </div>
      
      <div className="program-tabs">
        <button 
          className={`tab ${activeTab === 'details' ? 'active' : ''}`}
          onClick={() => setActiveTab('details')}
        >
          Details
        </button>
        <button 
          className={`tab ${activeTab === 'triggers' ? 'active' : ''}`}
          onClick={() => setActiveTab('triggers')}
        >
          Triggers
        </button>
        <button 
          className={`tab ${activeTab === 'preconditions' ? 'active' : ''}`}
          onClick={() => setActiveTab('preconditions')}
        >
          Preconditions
        </button>
      </div>
      
      <div className="program-content">
        {activeTab === 'details' && program && (
          <div className="program-info">
            <div className="info-section">
              <h3>Program Information</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Value Paid:</span>
                  <span className="info-value">${program && typeof program.value_paid === 'number' 
                      ? program.value_paid.toFixed(2) 
                      : parseFloat(String(program?.value_paid || '0')).toFixed(2)}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Start Date:</span>
                  <span className="info-value">
                    {program?.date_start 
                      ? format(new Date(program.date_start), 'MM/dd/yyyy')
                      : 'Not specified'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">End Date:</span>
                  <span className="info-value">
                    {program?.date_end 
                      ? format(new Date(program.date_end), 'MM/dd/yyyy')
                      : 'No end date'}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="info-section">
              <h3>Partner Information</h3>
              <div className="info-grid">
                <div className="info-item">
                  <label>Partner:</label>
                  <span>
                    {program?.partner ? (
                      <>
                        {program.partner.name} ({program.partner.code})
                      </>
                    ) : (
                      'No partner assigned'
                    )}
                  </span>
                </div>
                
                <div className="info-item">
                  <label>Partner Program ID:</label>
                  <span>{program?.partner_program_id || 'Not specified'}</span>
                </div>
                
                <div className="info-item">
                  <label>Description:</label>
                  <span>{program?.partner?.description || 'No description'}</span>
                </div>
                
                <div className="info-item">
                  <label>Address:</label>
                  <span>{program?.partner?.address || 'No address'}</span>
                </div>
              </div>
            </div>
            
            <div className="info-section">
              <h3>Program Description</h3>
              <p className="program-description">{program.description}</p>
            </div>
          </div>
        )}
        
        {activeTab === 'triggers' && program && (
          <div className="program-triggers">
            <h3>Trigger Configuration</h3>
            
            {triggerSaved && (
              <div className="success-message">
                Trigger saved successfully!
              </div>
            )}
            
            {successMessage && (
              <div className="success-message">
                {successMessage}
              </div>
            )}
            
            <div className="trigger-actions">
              <button 
                className="add-trigger-button"
                onClick={() => handleAddTrigger()}
              >
                + Add Trigger
              </button>
            </div>
            
            {editingTrigger && (
              <TriggerEditor
                programId={parseInt(programId)}
                triggerId={currentTriggerId}
                onSaved={handleTriggerSaved}
                onCancel={handleCancelTriggerEdit}
              />
            )}
            
            {!editingTrigger && (
              <div className="configured-triggers">
                <h4>Configured Triggers</h4>
                
                {programTriggers.length === 0 ? (
                  <div className="no-triggers-message">
                    No triggers configured yet. Click "Add Trigger" to create one.
                  </div>
                ) : (
                  <div className="trigger-chains">
                    {programTriggers.map((rootTrigger, index) => (
                      <div key={rootTrigger.id} className="trigger-chain">
                        <div className="trigger-chain-header">
                          <h5>Trigger Chain {index + 1}</h5>
                          {index > 0 && (
                            <div className="chain-relationship">
                              OR
                            </div>
                          )}
                        </div>
                        
                        <div className="trigger-chain-items">
                          {renderTriggerChain(rootTrigger)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'preconditions' && program && (
          <div className="program-preconditions">
            <h3>Preconditions</h3>
            <p>Preconditions determine who is eligible for this program.</p>
            
            {program.Preconditions && program.Preconditions.length > 0 ? (
              <div className="preconditions-list">
                {program.Preconditions.map(precondition => (
                  <div key={precondition.id} className="precondition-item">
                    <h4>{precondition.type}</h4>
                    <p>{precondition.value}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-preconditions-message">
                No preconditions configured. This program is open to everyone.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgramDetails; 